import React, { useState, useLayoutEffect } from 'react';
import classNames from 'classnames';
import { Form as FinalForm } from 'react-final-form';
import { useHistory } from 'react-router-dom';

// Contexts
import { useRouteConfiguration } from '../../../../context/routeConfigurationContext';
import { useConfiguration } from '../../../../context/configurationContext';

// Utility
import { FormattedMessage } from '../../../../util/reactIntl';
import { createResourceLocatorString } from '../../../../util/routes';
import { isOriginInUse, parseSelectFilterOptions } from '../../../../util/search';
import { stringifyDateToISO8601 } from '../../../../util/dates';

// Shared components
import { Form, PrimaryButton, IconSearch } from '../../../../components';

import FilterCategories from './FilterCategories/FilterCategories';
import FilterDateRange from './FilterDateRange/FilterDateRange';
import FilterLocation from './FilterLocation/FilterLocation';
import FilterKeyword from './FilterKeyword/FilterKeyword';
import FilterCountries from './FilterCountries/FilterCountries';
import FilterPrice from './FilterPrice/FilterPrice';

import css from './SearchCTA.module.css';

const DROPDOWN_OPEN_UPWARD_SCROLL_VH = 0.5;

const GRID_CONFIG = [
  { gridCss: css.gridCol1 },
  { gridCss: css.gridCol2 },
  { gridCss: css.gridCol3 },
  { gridCss: css.gridCol4 },
];

const getGridCount = numberOfFields => {
  const gridConfig = GRID_CONFIG[numberOfFields - 1];
  return gridConfig ? gridConfig.gridCss : GRID_CONFIG[0].gridCss;
};

const isEmpty = value => {
  if (value == null) return true;
  return value.hasOwnProperty('length') && value.length === 0;
};

const useDropdownOpenUpwardByScroll = () => {
  const [openUpward, setOpenUpward] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    const thresholdPx = window.innerHeight * DROPDOWN_OPEN_UPWARD_SCROLL_VH;
    return window.scrollY < thresholdPx;
  });

  useLayoutEffect(() => {
    const update = () => {
      const thresholdPx = window.innerHeight * DROPDOWN_OPEN_UPWARD_SCROLL_VH;
      setOpenUpward(window.scrollY < thresholdPx);
    };

    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  return openUpward;
};

const formatDateValue = (dateRange, queryParamName) => {
  const hasDates = dateRange;
  const { startDate, endDate } = hasDates ? dateRange : {};
  const start = startDate ? stringifyDateToISO8601(startDate) : null;
  const end = endDate ? stringifyDateToISO8601(endDate) : null;
  const value = start && end ? `${start},${end}` : null;
  return { [queryParamName]: value };
};

export const SearchCTA = React.forwardRef((props, ref) => {
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();
  const config = useConfiguration();

  const { listingsByFilters = {}, onCountListingsByFilters = () => { }, id, heroAsideTexts } = props;
  const { categories, dateRange, keywordSearch, locationSearch } = props.searchFields;

  const [submitDisabled, setSubmitDisabled] = useState(false);
  const [queryParams, setQueryParams] = useState({});
  const openDropdownUpward = useDropdownOpenUpwardByScroll();
  const dropdownOpenUpwardClass = openDropdownUpward ? css.dropdownOpenUpward : null;

  const categoryConfig = config.categoryConfiguration;
  // Keep all configured categories visible; counts may be 0 after other filters
  const showedCategories = categoryConfig.categories.map(item => ({
    ...item,
    count: listingsByFilters[item.id] ?? 0,
  }));

  const countryConfig =
    config.listing.listingFields.find(item => item.key === 'linstingFieldId_country') || {};
  const showedCountries = (countryConfig.enumOptions || [])
    .map(item => ({
      ...item,
      count: listingsByFilters['linstingFieldId_country']?.[item.option] ?? 0,
    }))
    .filter(item => item.count > 0);


  const priceConfig = config.search.defaultFilters.find(item => item.key === 'price');
  const { currencySymbol = '€' } = config;

  const filters = {
    countries: {
      enabled: true,
      // isValid: () => showedCountries.length > 0,
      isValid: () => true,
      render: alignLeft => (
        <div className={css.filterField} key="countries">
          <FilterCountries
            countries={showedCountries}
            alignLeft={alignLeft}
            onCountListingsByFilters={onCountListingsByFilters}
            queryParams={queryParams}
            setQueryParams={setQueryParams}
            dropdownOpenUpwardClass={dropdownOpenUpwardClass}
          />
        </div>
      ),
    },
    categories: {
      enabled: categories,
      // isValid: () => showedCategories.length > 0,
      isValid: () => true,
      render: alignLeft => (
        <div className={css.filterField} key="categories">
          <FilterCategories
            categories={showedCategories}
            alignLeft={alignLeft}
            onCountListingsByFilters={onCountListingsByFilters}
            queryParams={queryParams}
            setQueryParams={setQueryParams}
            dropdownOpenUpwardClass={dropdownOpenUpwardClass}
          />
        </div>
      ),
    },
    keywordSearch: {
      enabled: keywordSearch,
      isValid: () => keywordSearch,
      render: alignLeft => (
        <div className={css.filterField} key="keywordSearch">
          <FilterKeyword />
        </div>
      ),
    },
    locationSearch: {
      enabled: locationSearch,
      isValid: () => locationSearch,
      render: alignLeft => (
        <div className={css.filterField} key="locationSearch">
          <FilterLocation setSubmitDisabled={setSubmitDisabled} alignLeft={alignLeft} />
        </div>
      ),
    },

    dateRange: {
      enabled: dateRange,
      isValid: () => dateRange,
      render: alignLeft => (
        <div className={css.filterField} key="dateRange">
          <FilterDateRange config={config} alignLeft={alignLeft} />
        </div>
      ),
    },
    price: {
      enabled: true,
      isValid: () => true,
      render: alignLeft => (
        <div className={css.filterField} key="price">
          <FilterPrice
            priceConfig={priceConfig}
            alignLeft={alignLeft}
            onCountListingsByFilters={onCountListingsByFilters}
            queryParams={queryParams}
            setQueryParams={setQueryParams}
            currencySymbol={currencySymbol}
            dropdownOpenUpwardClass={dropdownOpenUpwardClass}
          />
        </div>
      ),
    },
  };

  const addFilters = filterOrder => {
    const enabledFilters = filterOrder.filter(
      key => filters[key]?.enabled && filters[key]?.isValid()
    );

    const totalEnabled = enabledFilters.length;

    return enabledFilters.map((key, index) => {
      const filter = filters[key];
      const isLast = index === totalEnabled - 1;
      const alignLeft = totalEnabled === 1 || !isLast;

      return filter.enabled && filter.isValid() ? filter.render(alignLeft) : null;
    });
  };

  // Count the number search fields that are enabled
  const fieldCountForGrid = Object.values(filters).filter(field => field.enabled && field.isValid())
    .length;

  //  If no search fields are enabled, we return null (Console won't allow you to enable 0 search fields)
  if (!fieldCountForGrid) {
    return null;
  }

  const onSubmit = values => {
    // Convert form values to query parameters
    let queryParams = {};

    Object.entries(values).forEach(([key, value]) => {
      if (!isEmpty(value)) {
        if (key == 'dateRange') {
          const { dates } = formatDateValue(value, 'dates');
          queryParams.dates = dates;
        } else if (key == 'location') {
          if (value.selectedPlace) {
            const {
              search,
              selectedPlace: { origin, bounds },
            } = value;
            queryParams.bounds = bounds;
            queryParams.address = search;

            if (isOriginInUse(config) && origin) {
              queryParams.origin = `${origin.lat},${origin.lng}`;
            }
          }
        } else if (key == 'price') {
          queryParams[key] = `${value.minValue},${value.maxValue}`;
        } else if (key === 'pub_linstingFieldId_country') {
          const codes = Array.isArray(value)
            ? value.filter(Boolean)
            : typeof value === 'string' && value.length > 0
            ? parseSelectFilterOptions(value).filter(Boolean)
            : [];
          if (codes.length > 0) {
            queryParams[key] = codes.join(',');
          }
        } else {
          queryParams[key] = value;
        }
      }
    });

    const to = createResourceLocatorString('SearchPage', routeConfiguration, {}, queryParams);
    // Use history.push to navigate without page refresh
    history.push(to);
  };

  const isHeroSearch = id === "marketplace-introduction";

  const asideContentLeft = heroAsideTexts?.find(t => t.blockId === "hero-left-text");
  const asideContentRight = heroAsideTexts?.find(t => t.blockId === "hero-right-text");

  return (
    <div className={classNames(
      css.searchBarContainer,
      getGridCount(fieldCountForGrid),
      { [css.searchBarContainerHero]: isHeroSearch }
    )}>

      {asideContentLeft && (
        <div id="hero-left-text">
          <p>{asideContentLeft?.text?.content}</p>
        </div>
      )}
      {asideContentRight && (
        <div id="hero-right-text">
          <p>{asideContentRight?.text?.content}</p>
        </div>
      )}

      <FinalForm
        onSubmit={onSubmit}
        {...props}
        render={({ fieldRenderProps, handleSubmit, values }) => {
          return (
            <Form
              role="search"
              onSubmit={handleSubmit}
              className={classNames(css.gridContainer, getGridCount(fieldCountForGrid))}
            >
              {addFilters([
                'categories',
                'countries',
                'price',
                'keywordSearch',
                'locationSearch',
                'dateRange',
              ])}

              <PrimaryButton disabled={submitDisabled} className={css.submitButton} type="submit">
                <IconSearch />
              </PrimaryButton>
            </Form>
          );
        }}
      />
    </div>
  );
});

SearchCTA.displayName = 'SearchCTA';
